import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { ScrollArea } from "../ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import {
  MATIERES,
  NIVEAUX,
  SESSION_TYPES,
  getMatiereInfo,
  getSessionTypeInfo,
  JournalStatusBadge,
  formatDate,
} from "./constants";
import { SessionEditorModal } from "./SessionEditorModal";
import { JournalStatsDialog, JournalStatsButton } from "./JournalStatsDialog";
import HourlyDistributionDialog from "./HourlyDistributionDialog";
import PeriodSummaryDialog from "./PeriodSummaryDialog";
import WeekScheduleConfigDialog from "./WeekScheduleConfigDialog";
import DayViewDialog from "./DayViewDialog";
import JournalEventsDialog from "./JournalEventsDialog";
import MonthlyCalendarDialog from "./MonthlyCalendarDialog";
import ContinuousTimeGrid from "./ContinuousTimeGrid";
import TimetableDialog from "./TimetableDialog";
import MobileJournalView from "./MobileJournalView";
import { SessionCard } from "./JournalSessionCard";
import { useJournalData } from "./useJournalData";
import {
  updateTimeSlots,
  resetTimeSlots,
  generateSessionVariants,
  createResourceFromSession,
  moveSession,
  generateSessionsFromTimetable,
  getWeekParity,
  getWeekSchedule,
  getHolidays,
  exportWeekPdf,
  exportSemainierPdf,
  exportCahierJournalWeekPdf,
} from "../../lib/api";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar,
  CalendarDays,
  Clock,
  FileText,
  Check,
  X,
  RotateCcw,
  Trash2,
  Copy,
  Settings,
  Target,
  Users,
  Sparkles,
  Loader2,
  GripVertical,
  Star,
  BarChart3,
  Move,
  PieChart,
  Wand2,
  Download,
  Printer,
  FileDown,
  Grid3X3,
  BookOpen,
  LayoutGrid,
  Rows3,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

// Default days (will be filtered based on week schedule config)
const ALL_DAYS = [
  { id: "lundi", label: "Lundi", index: 0 },
  { id: "mardi", label: "Mardi", index: 1 },
  { id: "mercredi", label: "Mercredi", index: 2 },
  { id: "jeudi", label: "Jeudi", index: 3 },
  { id: "vendredi", label: "Vendredi", index: 4 },
  { id: "samedi", label: "Samedi", index: 5 },
  { id: "dimanche", label: "Dimanche", index: 6 },
];

function getWeekDates(date, workingDays = ["lundi", "mardi", "mercredi", "jeudi", "vendredi"]) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  
  // Return days 0-6 but mark non-working days
  return ALL_DAYS.map((dayInfo) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + dayInfo.index);
    return {
      date: date.toISOString().split("T")[0],
      dayId: dayInfo.id,
      label: dayInfo.label,
      isWorking: workingDays.includes(dayInfo.id),
    };
  });
}

// Draggable wrapper for session cards - uses a drag handle instead of whole card
function DraggableSessionCard({ session, children, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: session.id,
    data: { session },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {/* Drag handle - only this area activates drag */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute top-1 right-1 z-10 p-1 rounded cursor-grab active:cursor-grabbing hover:bg-slate-200/50 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Glisser pour déplacer"
      >
        <GripVertical className="w-3 h-3 text-slate-400" />
      </div>
      {/* Card content - clickable for editing */}
      <div onClick={() => onEdit(session)} className="cursor-pointer">
        {children}
      </div>
    </div>
  );
}

// Droppable time slot cell
function DroppableSlotCell({ slotId, date, isToday, children }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `${date}-${slotId}`,
    data: { date, slotId },
  });

  return (
    <div
      ref={setNodeRef}
      data-testid={`slot-${date}-${slotId}`}
      className={`min-h-[100px] h-full p-1.5 rounded-lg border transition-colors flex flex-col ${
        isToday ? "bg-indigo-50/50 border-indigo-200" : "bg-slate-50/80 border-slate-200"
      } ${isOver ? "border-indigo-400 bg-indigo-100/50 ring-2 ring-indigo-300" : "hover:border-indigo-300"}`}
    >
      {children}
    </div>
  );
}

// SessionCard is now imported from ./JournalSessionCard.jsx

function TimeSlotsConfigDialog({ open, onOpenChange, timeSlots, onSave }) {
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSlots(timeSlots || []);
  }, [timeSlots]);

  const updateSlot = (index, field, value) => {
    const newSlots = [...slots];
    newSlots[index] = { ...newSlots[index], [field]: value };
    setSlots(newSlots);
  };

  const addSlot = () => {
    const newId = `slot_${Date.now()}`;
    setSlots([
      ...slots,
      { id: newId, label: `Creneau ${slots.length + 1}`, start_time: "08:00", end_time: "08:45", order: slots.length },
    ]);
  };

  const removeSlot = (index) => {
    setSlots(slots.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTimeSlots(slots.map((s, i) => ({ ...s, order: i })));
      toast.success("Creneaux enregistres");
      onSave(slots);
      onOpenChange(false);
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reinitialiser les creneaux par defaut ?")) return;
    try {
      const { data } = await resetTimeSlots();
      setSlots(data.slots);
      toast.success("Creneaux reinitialises");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configurer les creneaux horaires
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[400px]">
          <div className="space-y-2 pr-4">
            {slots.map((slot, index) => (
              <div key={slot.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
                <Input
                  value={slot.label}
                  onChange={(e) => updateSlot(index, "label", e.target.value)}
                  className="h-8 w-[120px]"
                  placeholder="Label"
                />
                <Input
                  type="time"
                  value={slot.start_time}
                  onChange={(e) => updateSlot(index, "start_time", e.target.value)}
                  className="h-8 w-[100px]"
                />
                <span className="text-slate-400">-</span>
                <Input
                  type="time"
                  value={slot.end_time}
                  onChange={(e) => updateSlot(index, "end_time", e.target.value)}
                  className="h-8 w-[100px]"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => removeSlot(index)}
                >
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>

        <Button variant="outline" size="sm" onClick={addSlot} className="mt-2">
          <Plus className="w-4 h-4 mr-1" />
          Ajouter un creneau
        </Button>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={handleReset}>
            Reinitialiser
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TeacherJournalTab() {
  const navigate = useNavigate();
  
  // Use the centralized journal data hook
  const {
    currentDate,
    weekDates,
    timeSlots,
    workingDays,
    sessionsByDate,
    loading,
    franceCalendar: holidays,
    teacherCycles,
    weekLabel,
    setTimeSlots,
    setWorkingDays,
    goToPrevWeek,
    goToNextWeek,
    goToToday,
    goToDate,
    fetchWeekData,
    getDateStatus,
    handleStatusChange,
    handleDelete,
    handleDuplicate,
    handleSaveSession,
  } = useJournalData();
  
  // Local UI state
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scheduleConfig, setScheduleConfig] = useState(null); // Full schedule config with grid_interval
  
  // View mode: 'slots' (classic) or 'continuous' (new time grid)
  const [viewMode, setViewMode] = useState("continuous");
  
  // Dialogs
  const [sessionEditorOpen, setSessionEditorOpen] = useState(false);
  const [timeSlotsConfigOpen, setTimeSlotsConfigOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [eventsDialogOpen, setEventsDialogOpen] = useState(false);
  const [monthlyCalendarOpen, setMonthlyCalendarOpen] = useState(false);
  const [timetableDialogOpen, setTimetableDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState(null); // For continuous grid
  const [editingSession, setEditingSession] = useState(null);
  const [mobileSelectedDate, setMobileSelectedDate] = useState(new Date()); // Mobile day view

  // Update current time every minute for the time indicator
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch schedule config for grid_interval (not in hook)
  useEffect(() => {
    const fetchScheduleConfig = async () => {
      try {
        const { data } = await getWeekSchedule();
        if (data) {
          setScheduleConfig(data);
        }
      } catch (err) {
        console.error("Error fetching schedule config:", err);
      }
    };
    fetchScheduleConfig();
  }, []);

  // Initialize mobile selected date to first working day or today if it's a working day
  useEffect(() => {
    if (weekDates.length === 0) return;
    const todayStr = new Date().toISOString().split("T")[0];
    const todayInWeek = weekDates.find(d => d.date === todayStr && d.isWorking);
    if (todayInWeek) {
      setMobileSelectedDate(new Date(todayStr));
    } else {
      const firstWorking = weekDates.find(d => d.isWorking);
      if (firstWorking) {
        setMobileSelectedDate(new Date(firstWorking.date));
      }
    }
  }, [weekDates]);

  // Calculate which slot index the current time falls into
  const getCurrentTimeSlotInfo = () => {
    const now = currentTime;
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentMinutes = hours * 60 + minutes;
    
    for (let i = 0; i < timeSlots.length; i++) {
      const slot = timeSlots[i];
      if (!slot.start_time || !slot.end_time) continue;
      
      const [startH, startM] = slot.start_time.split(":").map(Number);
      const [endH, endM] = slot.end_time.split(":").map(Number);
      const slotStart = startH * 60 + startM;
      const slotEnd = endH * 60 + endM;
      
      if (currentMinutes >= slotStart && currentMinutes <= slotEnd) {
        const percentage = ((currentMinutes - slotStart) / (slotEnd - slotStart)) * 100;
        return { slotIndex: i, percentage, time: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}` };
      }
    }
    return null;
  };

  // Callback when config is saved
  const handleConfigSaved = (newConfig) => {
    if (newConfig?.working_days) {
      setWorkingDays(newConfig.working_days);
    }
    // Refetch config and data
    fetchConfig();
    fetchWeekData();
  };

  // Use hook navigation functions
  const handlePrevWeek = goToPrevWeek;
  const handleNextWeek = goToNextWeek;

  const handleAddSession = (date, slotId) => {
    setSelectedDate(date);
    setSelectedSlot(slotId);
    setSelectedStartTime(null);
    setEditingSession(null);
    setSessionEditorOpen(true);
  };

  // Handler for adding session from continuous grid (with start time)
  const handleAddSessionAtTime = (date, startTime) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSelectedStartTime(startTime);
    setEditingSession(null);
    setSessionEditorOpen(true);
  };

  const handleEditSession = (session) => {
    setEditingSession(session);
    setSelectedDate(session.date);
    setSelectedSlot(session.time_slot_id);
    setSelectedStartTime(session.start_time);
    setSessionEditorOpen(true);
  };

  const handleGenerateVariants = async (sessionId) => {
    try {
      const { data } = await generateSessionVariants(sessionId);
      toast.success(`${data.variants_generated} variante(s) generee(s)`);
      fetchWeekData();
    } catch (err) {
      console.error("Error generating variants:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la generation");
    }
  };

  // Create resource from session and navigate to editor
  const handleCreateResource = async (sessionId) => {
    try {
      const { data } = await createResourceFromSession(sessionId);
      toast.success("Fiche créée !");
      // Navigate to editor with the new resource
      navigate(`/create/${data.id}`);
    } catch (err) {
      console.error("Error creating resource:", err);
      toast.error("Erreur lors de la creation de la fiche");
    }
  };

  // Open existing resource in editor
  const handleOpenResource = (resourceId) => {
    navigate(`/create/${resourceId}`);
  };

  const getSessionsForSlot = (date, slotId) => {
    const dateSessions = sessionsByDate[date] || [];
    return dateSessions.filter((s) => {
      if (s.time_slot_id === slotId) return true;
      // Also match by time if no slot ID
      const slot = timeSlots.find((ts) => ts.id === slotId);
      if (slot && s.start_time === slot.start_time) return true;
      return false;
    });
  };

  // Drag & Drop state
  const [activeSession, setActiveSession] = useState(null);
  
  // New dialogs state
  const [hourlyDistributionOpen, setHourlyDistributionOpen] = useState(false);
  const [periodSummaryOpen, setPeriodSummaryOpen] = useState(false);
  const [weekScheduleConfigOpen, setWeekScheduleConfigOpen] = useState(false);
  const [dayViewOpen, setDayViewOpen] = useState(false);
  const [dayViewDate, setDayViewDate] = useState(null);
  const [weekParity, setWeekParity] = useState(null);
  const [generatingSessions, setGeneratingSessions] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Open day view for a specific date
  const handleOpenDayView = (dateStr) => {
    setDayViewDate(new Date(dateStr));
    setDayViewOpen(true);
  };

  // Export week as PDF
  const handleExportWeek = async () => {
    const visibleDays = weekDates.filter(d => d.isWorking);
    if (visibleDays.length === 0) return;
    
    setExporting(true);
    try {
      const response = await exportWeekPdf(visibleDays[0].date);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cahier-journal-semaine-${visibleDays[0].date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export PDF téléchargé");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  // Export Semainier (compact grid)
  const handleExportSemainier = async () => {
    const visibleDays = weekDates.filter(d => d.isWorking);
    if (visibleDays.length === 0) return;
    
    setExporting(true);
    try {
      const response = await exportSemainierPdf(visibleDays[0].date);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `semainier-${visibleDays[0].date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Semainier téléchargé");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  // Export Cahier-journal (detailed daily plans)
  const handleExportCahierJournal = async () => {
    const visibleDays = weekDates.filter(d => d.isWorking);
    if (visibleDays.length === 0) return;
    
    setExporting(true);
    try {
      const response = await exportCahierJournalWeekPdf(visibleDays[0].date);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cahier-journal-${visibleDays[0].date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Cahier-journal téléchargé");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };
  
  // Fetch week parity
  useEffect(() => {
    if (weekDates.length > 0 && weekDates[0]?.date) {
      getWeekParity(weekDates[0].date)
        .then(res => setWeekParity(res.data))
        .catch(console.error);
    }
  }, [weekDates]);
  
  // Generate sessions from timetable
  const handleGenerateSessions = async () => {
    const visibleDays = weekDates.filter(d => d.isWorking);
    if (visibleDays.length === 0) return;
    
    const firstDate = visibleDays[0].date;
    const lastDate = visibleDays[visibleDays.length - 1].date;
    
    setGeneratingSessions(true);
    try {
      const res = await generateSessionsFromTimetable(firstDate, lastDate);
      if (res.data.sessions_created > 0) {
        toast.success(`${res.data.sessions_created} séance(s) créée(s)`);
        fetchWeekData();
      } else {
        toast.info("Aucune nouvelle séance à créer");
      }
    } catch (err) {
      console.error("Error generating sessions:", err);
      toast.error("Erreur lors de la génération");
    } finally {
      setGeneratingSessions(false);
    }
  };
  
  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px before drag starts
      },
    })
  );

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    const session = active.data.current?.session;
    if (session) {
      setActiveSession(session);
    }
  };

  // Handle drag end - move session to new slot
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveSession(null);
    
    if (!over) return;
    
    const session = active.data.current?.session;
    const dropData = over.data.current;
    
    if (!session || !dropData) return;
    
    const { date: newDate, slotId: newSlotId } = dropData;
    
    // Don't do anything if dropped in same slot
    if (session.date === newDate && session.time_slot_id === newSlotId) {
      return;
    }
    
    // Get the slot to find the time
    const slot = timeSlots.find(s => s.id === newSlotId);
    
    try {
      await moveSession(session.id, {
        date: newDate,
        time_slot_id: newSlotId,
        start_time: slot?.start_time,
        end_time: slot?.end_time,
      });
      toast.success("Séance déplacée !");
      fetchWeekData();
    } catch (err) {
      console.error("Error moving session:", err);
      toast.error("Erreur lors du déplacement");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 mb-1">Cahier-journal</h2>
          <p className="text-slate-500 text-sm">
            Planifiez vos séances et liez vos compétences officielles
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* Week parity indicator */}
          {weekParity && (
            <Badge 
              variant="outline" 
              className={`text-xs ${
                weekParity.parity === "pair" 
                  ? "bg-blue-50 text-blue-700 border-blue-200" 
                  : "bg-purple-50 text-purple-700 border-purple-200"
              }`}
            >
              S{weekParity.week_number} {weekParity.parity === "pair" ? "Paire" : "Impaire"}
            </Badge>
          )}
          
          {/* View mode toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === "continuous" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("continuous")}
              className="rounded-none h-8 px-2"
              title="Vue temporelle continue (redimensionnable)"
            >
              <Rows3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "slots" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("slots")}
              className="rounded-none h-8 px-2"
              title="Vue créneaux classique"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTimetableDialogOpen(true)}
            title="Configurer l'emploi du temps"
            className="hidden sm:flex border-emerald-200 hover:bg-emerald-50"
          >
            <BookOpen className="w-4 h-4 text-emerald-600" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateSessions}
            disabled={generatingSessions}
            title="Générer les séances depuis l'emploi du temps"
            className="hidden sm:flex"
          >
            {generatingSessions ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4" />
            )}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHourlyDistributionOpen(true)}
            title="Répartition horaire"
          >
            <PieChart className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPeriodSummaryOpen(true)}
            title="Vue période / Synthèse"
            className="border-emerald-200 hover:bg-emerald-50"
          >
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenDayView(new Date().toISOString().split("T")[0])}
            title="Vue jour"
          >
            <Calendar className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatsDialogOpen(true)}
            title="Statistiques"
          >
            <BarChart3 className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEventsDialogOpen(true)}
            title="Événements de la semaine"
            className="border-indigo-200 hover:bg-indigo-50"
          >
            <CalendarDays className="w-4 h-4 text-indigo-600" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonthlyCalendarOpen(true)}
            title="Calendrier mensuel"
            className="border-amber-200 hover:bg-amber-50"
          >
            <Calendar className="w-4 h-4 text-amber-600" />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={exporting}
                title="Exporter en PDF"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportSemainier}>
                <Grid3X3 className="w-4 h-4 mr-2" />
                Semainier (grille compacte)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCahierJournal}>
                <BookOpen className="w-4 h-4 mr-2" />
                Cahier-journal (détaillé)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekScheduleConfigOpen(true)}
            title="Configuration du temps scolaire"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrevWeek}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium min-w-[160px] text-center">
            {weekDates.length > 0 && weekDates[0]?.date && weekDates[weekDates.length - 1]?.date 
              ? `${formatDate(weekDates[0].date)} - ${formatDate(weekDates[weekDates.length - 1].date)}`
              : "Chargement..."
            }
          </span>
          <Button variant="outline" size="sm" onClick={handleNextWeek}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Week Grid - Desktop with Drag & Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className={`hidden md:block overflow-x-auto ${viewMode === "continuous" ? "!hidden" : ""}`}>
          <div className="min-w-[800px]">
            {/* Header - Only show working days */}
            {(() => {
              const visibleDays = weekDates.filter(d => d.isWorking);
              const numDays = visibleDays.length;
              // Use minmax for consistent column sizing
              const gridCols = `100px repeat(${numDays}, minmax(150px, 1fr))`;
              
              return (
                <>
                  <div 
                    className="grid gap-2 mb-2 sticky top-0 bg-white z-10 pb-2" 
                    style={{ gridTemplateColumns: gridCols }}
                  >
                    <div className="p-2 text-xs font-semibold text-slate-500 flex items-center justify-center bg-slate-50 rounded-lg">
                      Horaire
                    </div>
                    {visibleDays.map((dayInfo) => {
                      const dateStr = dayInfo.date;
                      const isToday = dateStr === new Date().toISOString().split("T")[0];
                      const dateStatus = getDateStatus(dateStr);
                      
                      return (
                        <div
                          key={dayInfo.dayId}
                          className={`p-3 text-center rounded-xl transition-all relative ${
                            dateStatus.isHoliday
                              ? "bg-gradient-to-br from-orange-100 to-amber-100 border-2 border-orange-300"
                              : isToday 
                                ? "bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg" 
                                : "bg-slate-50 border border-slate-200"
                          }`}
                        >
                          <div className={`text-xs font-medium ${
                            dateStatus.isHoliday 
                              ? "text-orange-600" 
                              : isToday ? "text-indigo-100" : "text-slate-400"
                          }`}>
                            {dayInfo.label}
                          </div>
                          <div className={`text-xl font-bold ${
                            dateStatus.isHoliday 
                              ? "text-orange-700" 
                              : isToday ? "text-white" : "text-slate-700"
                          }`}>
                            {new Date(dateStr).getDate()}
                          </div>
                          {/* Holiday/Vacation indicator */}
                          {dateStatus.isHoliday && (
                            <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-medium shadow">
                              {dateStatus.type === "ferie" ? "Férié" : "Vacances"}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Time Slots */}
                  {loading ? (
                    <div className="space-y-2">
                      {[...Array(7)].map((_, i) => (
                        <Skeleton key={i} className="h-24 w-full" />
                      ))}
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Current time indicator */}
                      {(() => {
                        const timeInfo = getCurrentTimeSlotInfo();
                        const todayIndex = visibleDays.findIndex(d => d.date === new Date().toISOString().split("T")[0]);
                        
                        if (timeInfo && todayIndex >= 0) {
                          // Calculate top position based on slot index and percentage within slot
                          const slotHeight = 108; // min-h-[100px] + margin
                          const topOffset = (timeInfo.slotIndex * slotHeight) + (timeInfo.percentage / 100 * slotHeight);
                          
                          return (
                            <div 
                              className="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
                              style={{ top: `${topOffset}px` }}
                            >
                              <div className="w-[100px] flex justify-end pr-2">
                                <span className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded font-mono">
                                  {timeInfo.time}
                                </span>
                              </div>
                              <div className="flex-1 flex items-center">
                                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                <div className="flex-1 h-0.5 bg-red-500" />
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      
                      {timeSlots.map((slot, slotIndex) => (
                      <div
                        key={slot.id}
                        className="grid gap-2 mb-2 items-stretch"
                        style={{ gridTemplateColumns: gridCols }}
                      >
                        <div className="p-2 text-xs text-slate-500 flex flex-col justify-center bg-slate-50 rounded-lg border border-slate-100 min-h-[100px]">
                          <span className="font-semibold text-slate-700">{slot.label}</span>
                          <span className="text-[10px] text-slate-400 mt-0.5">
                            {slot.start_time} - {slot.end_time}
                          </span>
                        </div>
                        {visibleDays.map((dayInfo) => {
                          const dateStr = dayInfo.date;
                          const sessions = getSessionsForSlot(dateStr, slot.id);
                          const isToday = dateStr === new Date().toISOString().split("T")[0];
                          const dateStatus = getDateStatus(dateStr);
                          
                          // If it's a holiday, show a special cell
                          if (dateStatus.isHoliday) {
                            return (
                              <div
                                key={`${dateStr}-${slot.id}`}
                                className="min-h-[100px] h-full p-2 rounded-lg border-2 border-dashed border-orange-200 bg-orange-50/50 flex items-center justify-center"
                              >
                                <span className="text-xs text-orange-400 text-center">
                                  {dateStatus.name}
                                </span>
                              </div>
                            );
                          }
                          
                          return (
                            <DroppableSlotCell
                              key={`${dateStr}-${slot.id}`}
                              slotId={slot.id}
                              date={dateStr}
                              isToday={isToday}
                            >
                              <div className="space-y-1.5 flex-1">
                                {sessions.map((session) => (
                                  <DraggableSessionCard key={session.id} session={session} onEdit={handleEditSession}>
                                    <SessionCard
                                      session={session}
                                      onStatusChange={handleStatusChange}
                                      onDelete={handleDelete}
                                      onDuplicate={handleDuplicate}
                                      onEdit={handleEditSession}
                                      onGenerateVariants={handleGenerateVariants}
                                      onCreateResource={handleCreateResource}
                                      onOpenResource={handleOpenResource}
                                    />
                                  </DraggableSessionCard>
                                ))}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full h-6 text-xs text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 mt-auto"
                                onClick={() => handleAddSession(dateStr, slot.id)}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Ajouter
                              </Button>
                            </DroppableSlotCell>
                          );
                        })}
                      </div>
                    ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Drag Overlay - shows floating card while dragging */}
        <DragOverlay>
          {activeSession ? (
            <div className="opacity-90 shadow-xl">
              <SessionCard
                session={activeSession}
                isDragging={true}
                onStatusChange={() => {}}
                onDelete={() => {}}
                onDuplicate={() => {}}
                onEdit={() => {}}
                onGenerateVariants={() => {}}
                onCreateResource={() => {}}
                onOpenResource={() => {}}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Continuous Time Grid View (when viewMode === "continuous") */}
      {viewMode === "continuous" && !loading && (
        <div className="hidden md:block">
          <ContinuousTimeGrid
            weekDates={weekDates}
            sessionsByDate={sessionsByDate}
            config={scheduleConfig}
            holidays={holidays}
            onAddSession={handleAddSessionAtTime}
            onEditSession={handleEditSession}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onGenerateVariants={handleGenerateVariants}
            onCreateResource={handleCreateResource}
            onOpenResource={handleOpenResource}
            onSessionsUpdated={fetchWeekData}
          />
        </div>
      )}

      {/* Mobile List View - Smartphone optimized */}
      <div className="md:hidden">
        {loading ? (
          <div className="space-y-3 p-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : (
          <MobileJournalView
            weekDates={weekDates}
            sessionsByDate={sessionsByDate}
            selectedDate={mobileSelectedDate}
            onDateChange={setMobileSelectedDate}
            onAddSession={handleAddSession}
            onEditSession={handleEditSession}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            getDateStatus={getDateStatus}
            timeSlots={timeSlots}
            loading={loading}
          />
        )}
      </div>

      {/* Session Editor Modal */}
      <SessionEditorModal
        open={sessionEditorOpen}
        onOpenChange={setSessionEditorOpen}
        session={editingSession}
        selectedDate={selectedDate}
        selectedSlot={selectedSlot}
        timeSlots={timeSlots}
        teacherCycles={teacherCycles}
        onSave={fetchWeekData}
      />

      {/* Time Slots Config Dialog */}
      <TimeSlotsConfigDialog
        open={timeSlotsConfigOpen}
        onOpenChange={setTimeSlotsConfigOpen}
        timeSlots={timeSlots}
        onSave={(newSlots) => {
          setTimeSlots(newSlots);
          fetchWeekData();
        }}
      />

      {/* Statistics Dialog */}
      <JournalStatsDialog
        open={statsDialogOpen}
        onOpenChange={setStatsDialogOpen}
      />

      {/* Hourly Distribution Dialog */}
      <HourlyDistributionDialog
        open={hourlyDistributionOpen}
        onOpenChange={setHourlyDistributionOpen}
      />

      {/* Week Schedule Config Dialog */}
      <WeekScheduleConfigDialog
        open={weekScheduleConfigOpen}
        onOpenChange={setWeekScheduleConfigOpen}
        onConfigSaved={handleConfigSaved}
      />

      {/* Day View Dialog */}
      <DayViewDialog
        open={dayViewOpen}
        onOpenChange={setDayViewOpen}
        initialDate={dayViewDate}
        onEditSession={handleEditSession}
        onStartSession={(session) => {
          if (session.resource_id) {
            navigate(`/editor/${session.resource_id}`);
          }
        }}
      />

      {/* Events Dialog (Phase 3) */}
      <JournalEventsDialog
        open={eventsDialogOpen}
        onOpenChange={setEventsDialogOpen}
        initialDate={currentDate}
      />

      {/* Monthly Calendar Dialog */}
      <MonthlyCalendarDialog
        open={monthlyCalendarOpen}
        onOpenChange={setMonthlyCalendarOpen}
      />
      
      {/* Timetable Dialog */}
      <TimetableDialog
        open={timetableDialogOpen}
        onOpenChange={setTimetableDialogOpen}
        onTimetableSaved={fetchWeekData}
      />

      {/* Period Summary Dialog */}
      <PeriodSummaryDialog
        open={periodSummaryOpen}
        onOpenChange={setPeriodSummaryOpen}
        cycle={teacherCycles?.[0] || "cycle2"}
      />
    </div>
  );
}
